import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { Select, SelectChangeEvent, SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ApiService } from 'src/app/core/services/api.service';
import { ConfigurationService } from 'src/app/core/services/configuration.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { LaborSituation } from 'src/app/models/LaborSituation';
import { NewRequestStatus } from 'src/app/models/NewRequestStatus';
import { DocumentationPack } from 'src/app/models/OpenData.DocumentationPack';
import { LegalPersonQueryResponse } from 'src/app/models/OpenData.LegalPersonQueryResponse';
import { PhysicalPersonQueryResponse } from 'src/app/models/OpenData.PhysicalPersonQueryResponse';
import { StandardRequiredService } from 'src/app/models/StandardRequiredService';
import { AdminMessagesComponent, AdminMessagesType } from 'src/app/shared/components/admin-messages/admin-messages.component';
import { BelenderWidgetComponent } from 'src/app/shared/components/belender-widget/belender-widget.component';
import { QrCodeComponent } from 'src/app/shared/components/qr-code/qr-code.component';
import { DNI, INVALID, SEPARATOR } from 'src/app/shared/constants/validation.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { InsideDisableComponent } from '../../../utils/inside-disable/inside-disable.component';
import { LoadingComponent } from '../../../utils/loading/loading.component';
import { StandardRequestService } from '../standard-requests/standard-request.service';
import { StandardRequestsFilterService } from '../standard-requests/standard-requests-filter.service';
import { NewStandardRequestService } from './new-standard-request.service';
import { LabeledValue } from 'src/app/models/LabeledValue';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-new-standard-request',
    templateUrl: './new-standard-request.component.html',
    styleUrl: './new-standard-request.component.scss',
    standalone: true,
    imports: [
        ToastModule,
        PrimeTemplate,
        NgFor,
        ConfirmDialogModule,
        NgIf,
        RadioButtonModule,
        FormsModule,
        TooltipModule,
        LoadingComponent,
        InsideDisableComponent,
        ReactiveFormsModule,
        InputTextModule,
        DatePickerModule,
        SelectModule,
        TranslateModule,
        QrCodeComponent,
        AdminMessagesComponent,
        BelenderWidgetComponent
    ],
})
export class NewStandardRequestComponent implements OnInit, OnDestroy {

    physicalPersonQueryResponse: PhysicalPersonQueryResponse | LegalPersonQueryResponse;

    @ViewChild('docPackDropdown') docPackDropdown: Select;
    @ViewChild('adminMessages') adminMessagesComponent: AdminMessagesComponent;

    @Input() newRequestStatus: NewRequestStatus = { title: 'New request', step: 1 };
    @Input() trustedConsentUrl: any;

    isLoading = false;
    formIsDisabled = false;

    docPacks: DocumentationPack[] = [];
    availableDocPacks: DocumentationPack[] = [];
    helpTexts: Record<string, Record<string, string>> = {};
    accessTypeHelpText = '';
    belenderWidgetLangOptions: LabeledValue[] = [];
    showWidgetLanguageSelector = false; 

    currentBelenderCircuit: 2 | 4 | 6 = 2;
    currentPersonType: '00' | '01' | '02' = '00';

    private readonly waitingMessagesTranslationKeys = ['NEW_REQUEST.WAITING_MESSAGES.WAIT_1', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_2', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_3'];
    waitingMessages = [];

    private invalidFields = [];
    private requiredFieldsMissing = false;  

    readonly services: StandardRequiredService[] = [
        { id: 'Clave_Pin', label: 'CLAVE_MOVIL' },
        { id: 'SMS', label: 'SMS' },
        { id: 'Client_Certificate', label: 'DIG_CERT_CC' }
    ];
    availableServices: StandardRequiredService[] = this.services.filter(service => service.id !== 'Client_Certificate');
    selectedRequestorType: 'Physical_Person' | 'Legal_Person' | '' = 'Physical_Person';
    selectedRequestType = this.services[0].id;

    @Input() belenderStatus: {
        actualStatus: string;
        actualStatusMessage: string;
    } = { actualStatus: '', actualStatusMessage: '' };

    isLoadingRequest = false;

    adminMessagesType: AdminMessagesType = 'clave';
    isAdminOk = true;

    private readonly destroy$ = new Subject<void>();

    readonly laborSituations: LaborSituation[] = [{ id: '00', label: 'PERSON_TYPES.00_ABBRV' }, { id: '01', label: 'PERSON_TYPES.01_ABBRV' }, { id: '02', label: 'PERSON_TYPES.02_ABBRV' }];

    constructor(
        private readonly api: ApiService,
        private readonly router: Router,
        private readonly translateService: TranslateService,
        private readonly toastService: ToastService,
        public readonly standardRequestService: StandardRequestService,
        public readonly newStandardRequestService: NewStandardRequestService,
        private readonly sanitizer: DomSanitizer,
        private readonly configurationService: ConfigurationService,
        private readonly utilsService: UtilsService,
        public readonly standardRequestsFilterService: StandardRequestsFilterService
    ) { }

    async ngOnInit(): Promise<void> {
        this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.filterAvailableDocPacks();
            this.updateAccessTypeHelpText();
        });
        this.waitingMessages = this.waitingMessagesTranslationKeys.map(key => this.translateService.instant(key));
        await this.fetchUserDocPacks();
        await this.fetchHelpTexts();
        await this.fetchWidgetLanguages();
        await this.fetchDatesRequired();
        this.newStandardRequestService.resetRequestorAndRequestTypes();
        this.initStepOne();
        this.syncSelectionByPersonType();
        this.filterAvailableDocPacks(true, false);
        this.updateAdminMessagesType();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateAdminMessagesType(): void {
        switch (this.selectedRequestType) {
            case 'SMS':
                this.adminMessagesType = 'sms';
                break;
            case 'Client_Certificate':
                this.adminMessagesType = 'clientCertificatePermissive';
                break;
            default:
                this.adminMessagesType = 'clave';
                break;
        }
    }

    private setBelenderCircuitByService(requestType: string): void {
        switch (requestType) {
            case 'SMS':
                this.currentBelenderCircuit = 4;
                break;
            case 'Client_Certificate':
                this.currentBelenderCircuit = 6;
                break;
            default:
                this.currentBelenderCircuit = 2;
                break;
        }
    }

    private syncSelectionByPersonType(): void {
        if (this.currentPersonType === '02') {
            this.selectedRequestorType = 'Legal_Person';
            this.availableServices = this.services.filter(service => service.id === 'Client_Certificate');
            this.selectedRequestType = 'Client_Certificate';
            this.currentBelenderCircuit = 6;
        } else {
            this.selectedRequestorType = 'Physical_Person';
            this.availableServices = this.services.filter(service => service.id !== 'Client_Certificate');

            if (this.selectedRequestType === 'Client_Certificate') {
                this.selectedRequestType = 'Clave_Pin';
            }

            this.setBelenderCircuitByService(this.selectedRequestType);
        }

        this.newStandardRequestService.setForm(this.selectedRequestorType, this.selectedRequestType);
        this.updateAdminMessagesType();
        this.updateAccessTypeHelpText();
    }

    async fetchUserDocPacks(): Promise<void> {
        try {
            this.docPacks = await this.utilsService.getUserDocumentationPacks();
        } catch {
            // the setTimeout is used due to NgZone
            setTimeout(() => {
                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.FETCH_DOCPACKS_ERROR', true, null, 'docPackError');
            }, 0);
        }
    }

    async fetchHelpTexts(): Promise<void> {
        try {
            this.helpTexts = await this.utilsService.getHelpTexts();
        } catch {
            this.helpTexts = {};
        }
    }

    async fetchWidgetLanguages(): Promise<void> {
        try {
            this.belenderWidgetLangOptions = await this.utilsService.getAllowedWidgetLanguages();
            this.showWidgetLanguageSelector = await this.utilsService.isWidgetLanguageSelectorVisible();            
        } catch {
            // the setTimeout is used due to NgZone
            setTimeout(() => {
                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.FETCH_WIDGET_LANGUAGES_ERROR', true, null, 'widgetLangError');
            }, 0);
        }
    }

    async fetchDatesRequired(): Promise<void> {
        try {
            this.newStandardRequestService.datesRequired = await this.utilsService.areStandardDatesRequired();
        } catch {
            this.newStandardRequestService.datesRequired = true;
        }
    }

    filterAvailableDocPacks(forceLoadDocPack = false, resetDropdown = true): void {
        if (this.newRequestStatus.step === 1) {
            this.availableDocPacks = this.docPacks
                .filter(docPack => docPack.belenderCircuits.includes(this.currentBelenderCircuit) && docPack.personTypes.includes(this.currentPersonType))
                .map(docPack => ({ ...docPack, localizedLabel: this.getLocalizedDocPackLabel(docPack) }));

            if (forceLoadDocPack && this.availableDocPacks.length > 0) {
                this.newStandardRequestService.myForm.get('documentationPack').setValue(this.availableDocPacks[0].id);
            }

            if (resetDropdown && this.availableDocPacks.length === 0) {
                this.newStandardRequestService.myForm.get('documentationPack').setValue('');
                this.docPackDropdown.clear();
            }
        }
    }

    private getLocalizedDocPackLabel(docPack: DocumentationPack): string {
        return this.resolveLocalizedText(docPack.labelLanguaje) ?? docPack.label ?? docPack.id;
    }

    private updateAccessTypeHelpText(): void {
        this.accessTypeHelpText = this.resolveLocalizedText(this.helpTexts[this.selectedRequestType]) ?? '';
    }

    private resolveLocalizedText(texts: Record<string, string> | null | undefined): string | undefined {
        if (!texts) return undefined;
        return texts[this.translateService.currentLang] ?? texts[this.translateService.defaultLang] ?? Object.values(texts)[0];
    }

    // receiveMessage = (event: MessageEvent) => {
    //     const messageFromBelender = event.data as BelenderWebShocketMessage;

    //     if (messageFromBelender && messageFromBelender.type == "websocket") {
    //         if (BelenderStatusCodes.includes(messageFromBelender.data.status_code)) {
    //             // Assert form está deshabilitado
    //             if (!this.formIsDisabled) this.formIsDisabled = true;

    //             this.belenderStatus.actualStatus = messageFromBelender.data.status_code;
    //             this.belenderStatus.actualStatusMessage = messageFromBelender.data.status_message;

    //             if (this.belenderStatus.actualStatus === "consent_process_completed") {
    //                 this.toastService.showSuccess('TOAST.SUCCESS', 'NEW_REQUEST.CREATED', true);
    //             }
    //         }
    //     }
    // };

    detailButtonIsVisible(): boolean {
        return this.belenderStatus.actualStatus === "consent_process_completed";
    };

    updateTrustedUrl(newUrl: string): void {
        this.trustedConsentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newUrl);
    }

    initStepOne(): void {
        if (this.newRequestStatus.step === 1) {
            if (this.selectedRequestorType != '' && this.selectedRequestType != '') {
                this.newStandardRequestService.setForm(this.selectedRequestorType, this.selectedRequestType);
            }
        }
    }

    goToLastStep(): void {
        if (this.newStandardRequestService.isFinishingOTP) {
            this.newStandardRequestService.isFinishingOTP = false;
            window.location.reload();
        }
        this.newRequestStatus = { title: 'Request summary', step: 2 };
    }

    continueOTP() {
        this.newRequestStatus = { title: 'Continue OTP', step: 3};
    }

    goToDetail(): void {
        if (this.physicalPersonQueryResponse) {
            this.isLoadingRequest = true;
            this.router.navigate([`/standard/request-detail/${this.physicalPersonQueryResponse.queryId}`])
                .catch(error => { console.error(error); })
                .finally(() => this.isLoadingRequest = true);
        }
    }


    //the validation is moved to the confirmSubmit method
    async onSubmit(): Promise<void> {
        this.formIsDisabled = true;

        if (this.newStandardRequestService.myForm.valid) {
            try {
                this.isLoading = true;
                const _myFormCopy = { ...this.newStandardRequestService.myForm.value };

                if (this.newStandardRequestService.myForm.get('birthDate')) {
                    // modifying the birthDate to the correct format
                    const _rawBirthDate = this.newStandardRequestService.myForm.value.birthDate;
                    _myFormCopy.birthDate = _rawBirthDate
                        ? this.utilsService.formatLocalYYYYMMDD(new Date(_rawBirthDate))
                        : null;
                }

                if (this.newStandardRequestService.myForm.get('personIdExpirationDate') && this.selectedRequestType === 'Clave_Pin') {
                    // modifying the personIdExpirationDate to the correct format
                    const _rawPersonIdExpirationDate = this.newStandardRequestService.myForm.value.personIdExpirationDate;
                    _myFormCopy.personIdExpirationDate = _rawPersonIdExpirationDate
                        ? this.utilsService.formatLocalYYYYMMDD(new Date(_rawPersonIdExpirationDate))
                        : null;
                }

                if (this.utilsService.isStringNullOrEmpty(_myFormCopy.secondSurname)) {
                    _myFormCopy.secondSurname = '-';
                }

                if (_myFormCopy.personId) {
                    _myFormCopy.personId = _myFormCopy.personId.toUpperCase();
                } else {
                    delete _myFormCopy.personId;
                }

                if (_myFormCopy.companyId) {
                    _myFormCopy.companyId = _myFormCopy.companyId.toUpperCase();
                }

                this.newStandardRequestService.myForm.get('personType').setValue(this.currentPersonType);
                _myFormCopy.personType = this.currentPersonType;
                _myFormCopy.belenderCircuit = this.currentBelenderCircuit;
                _myFormCopy.clientEnvironment = this.configurationService.getConfiguration().clientEnviroment;
                _myFormCopy.sendLink = this.getSendLink();

                if (this.selectedRequestType === 'Client_Certificate' && _myFormCopy.companyId) {
                    _myFormCopy.companyId = _myFormCopy.companyId.toUpperCase();
                }

                const endpoint = this.selectedRequestType === 'Client_Certificate'
                    ? 'standard/new-legal-request'
                    : 'standard/new-request';

                this.physicalPersonQueryResponse = await this.api.post<PhysicalPersonQueryResponse | LegalPersonQueryResponse>(endpoint, _myFormCopy);

                if (!this.validateResponse(this.physicalPersonQueryResponse as PhysicalPersonQueryResponse)) {
                    this.isLoading = false;
                    this.formIsDisabled = false;
                    return;
                }

                if (this.newStandardRequestService.requestType !== 'Clave_Pin_SMS' && this.physicalPersonQueryResponse.consentUrl != null) {
                    this.updateTrustedUrl(this.physicalPersonQueryResponse.consentUrl);
                }

                this.isLoading = false;
                this.newRequestStatus = { title: 'Request summary', step: 2 };

                await this.standardRequestsFilterService.search(true);
            } catch (error) {
                this.isLoading = false;
                this.formIsDisabled = false;
                if (error?.status === 422 || error?.error?.status === 422) {
                    this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.422_ERROR', true);
                    await this.adminMessagesComponent.setMessages();
                } else if (error?.status === 400 && error.error === INVALID + SEPARATOR + DNI) {
                    this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.INVALID_DNI', true);
                } else {
                    this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.ERROR', true);
                }
            }
        } else {
            this.formIsDisabled = false;
            this.checkValidationErrors();
        }
    }

    private getSendLink(): boolean {
        if (this.newStandardRequestService.requestType === 'Clave_Pin_SMS') {
            return true;
        }
        return false;
    }

    checkValidationErrors(): void {
        for (const key in this.newStandardRequestService.myForm.controls) { // LOOP THROUGH ALL FORM CONTROLS
            if (Object.prototype.hasOwnProperty.call(this.newStandardRequestService.myForm.controls, key)) { // CHECK IF THE PROPERTY BELONGS TO THE OBJECT
                const control = this.newStandardRequestService.myForm.get(key);
                if (control.invalid) {
                    this.checkFieldIsRequiredOrInvalid(control, key);
                }
            }
        }
        this.showValidationErrors();

        this.invalidFields = [];
        this.requiredFieldsMissing = false;
    }

    checkFieldIsRequiredOrInvalid(control: AbstractControl, key: string): void {
        if (control.errors['required']) {
            if (!this.requiredFieldsMissing) this.requiredFieldsMissing = true; // SET THE FLAG TO TRUE ONLY ONCE
            const formField = document.querySelector(`[formControlName="${key}"]`);
            formField.classList.add('ng-dirty', 'ng-touched'); // ADD THE CLASSES TO SHOW THE ERROR
        } else {
            this.invalidFields.push(`NEW_REQUEST.INVALID_FORM.${key}`);  // ADD THE INVALID FIELD TO THE ARRAY
        }
    }

    /**
     * if a required field is not filled, it shows a toast with a message,
     * otherwise it shows a toast with the invalid fields
     */
    showValidationErrors(): void {
        if (this.requiredFieldsMissing) {
            this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.ALL_FIELDS_REQUIRED', false, null, 'newRequestErrors');
        } else {
            this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.INVALID_FORM_DETAIL', false, 5000, 'newRequestErrors', this.invalidFields);
        }
    }

    validateResponse(physicalPersonQueryResponse: PhysicalPersonQueryResponse): boolean {
        if (physicalPersonQueryResponse.statusCode == 400 || physicalPersonQueryResponse.queryId == null) {
            this.toastService.showError(`Error ${physicalPersonQueryResponse.statusCode}`, `${physicalPersonQueryResponse?.formattedErrors}`, false, 10000);  //TODO: traudcir
            return false;
        }

        return true;
    }

    isFieldInvalid(fieldName: string, errorName: string): boolean {
        const field = this.newStandardRequestService.myForm.get(fieldName);

        if (field.hasError(errorName) && (field.dirty || field.touched)) {
            return true;
        } else {
            return false;
        }
    }

    onRequiredServiceChange(event: SelectChangeEvent): void {
        this.selectedRequestType = event.value;
        this.setBelenderCircuitByService(this.selectedRequestType);
        this.newStandardRequestService.setForm(this.selectedRequestorType, this.selectedRequestType);
        this.filterAvailableDocPacks(true);
        this.updateAdminMessagesType();
        this.updateAccessTypeHelpText();
    }

    onLaborSituationChange(event: SelectChangeEvent): void {
        this.currentPersonType = event.value;
        this.syncSelectionByPersonType();
        this.filterAvailableDocPacks(true);
    }

    showEmailField(): boolean {
        return this.newStandardRequestService.myForm.get('email')?.value !== '';
    }
}
